import React from 'react';
import { Column } from 'react-table';
import { ExecutionCard } from './ExecutionCard';
import { Link } from 'react-router-dom';
import { FlowExecution } from '../common/dataTypes/Jetstream';
import { useDisplayPreferences } from '../common/hooks/useDisplayPreferences';
import { formatDateWithTz } from '../common/utils/dateFormat';
import { ConfirmAction, Spinner, Capsule, Breakpoint, Small, Card, Large, JetTable } from '../jet-ui';

/** Parses a .NET datetime string, truncating fractional seconds to 3 digits and ensuring UTC interpretation. */
const parseDate = (s: string) => {
  let normalized = s.replace(/(\.\d{3})\d+/, '$1');
  if (!/[Zz]|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(normalized)) normalized += 'Z';
  return new Date(normalized);
};

const LiveRunTime: React.FC<{ started: string }> = ({ started }) => {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, Math.floor((Date.now() - parseDate(started).getTime()) / 1000));
  const h = String(Math.floor(diff / 3600)).padStart(2, '0');
  const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
  const s = String(diff % 60).padStart(2, '0');
  return <span>{h}:{m}:{s}</span>;
};

interface RunningTableProps {
  data: FlowExecution[];
  onStop: (flowExecutionId: number) => Promise<void>;
  loading?: boolean;
};

export const RunningTable: React.FC<RunningTableProps> = (props) => {

  const { prefs } = useDisplayPreferences();
  const tz = prefs.timezone;
  const onStop = props.onStop;
  const columns = React.useMemo((): Column<FlowExecution>[] => [
    {
      Header: '',
      id: 'actions',
      accessor: 'flowName',
      Cell: cell => (
        cell.row.original.status === 'Canceling' 
        ? <Spinner size={28} /> 
        : <ConfirmAction title="Cancel Flow Execution" value={cell.row.original} icon="block" onAction={(value, confirmed) => confirmed && onStop(value.flowExecutionId)}>
          Are you sure you wish to cancel {cell.row.original.flowName} execution {cell.row.original.flowExecutionId}?
        </ConfirmAction>
      ),
      width: 32
    },
    {
      Header: 'Flow',
      accessor: 'flowName',
      width: '20%',
      Cell: cell => (<Link to={`/execution/${cell.row.original.flowExecutionId}/detail`}>{cell.value}</Link>)
    },
    {
      Header: 'ID',
      accessor: 'flowExecutionId',
      width: '70px'
    },
    {
      Header: 'Jobs',
      accessor: execution => execution.structure.jobs.length,
      width: '60px'
    },
    {
      Header: 'Started',
      accessor: 'started',
      width: '18%',
      Cell: cell => formatDateWithTz(cell.value, tz)
    },
    {
      Header: 'Run Duration',
      id: 'runTime',
      accessor: 'started',
      width: '12%',
      Cell: cell => <LiveRunTime started={cell.value} />
    },
    {
      Header: 'By',
      accessor: 'userId',
      width: '14%'
    },
    {
      Header: 'Status',
      accessor: 'status',
      width: '100px',
      Cell: ({value}) => (<Capsule color={getStatusColor(value)}>{value}</Capsule>)
    }
  ], [onStop, tz]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Succeeded':
        return 'good';
      case 'Running':
      case 'Canceling':
        return 'warning';
    }

    return 'error';
  }

  return (
    <Breakpoint>
      <Small>
        {props.loading && <div className="Monitor__Loading"><Spinner /></div>}
        {props.data.map(execution => <ExecutionCard execution={execution} />)}
        {(props.data.length === 0 && !props.loading) && <Card>No flows currently executing.</Card>}
      </Small>
      <Large>
        <JetTable data={props.data} columns={columns} rowKey={(execution) => execution.flowExecutionId} emptyMessage="No flows currently executing" loading={props.loading} />
      </Large> 
    </Breakpoint>
  );
};
